import * as React from "react";
import {FormattedPageTitle} from "@/components/pages";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {Typography} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import Section from "@/components/Section";
import {useQuery} from "@apollo/client";
import Loading from "@/components/breadcrumbs/Loading";
import TargetSelector, {Target, TargetType} from "@/components/TargetSelector";
import {Link} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import DeleteCallGroupForm, {ReferenceType} from "@/components/call-group/delete/DeleteCallGroupForm";
import {
    CallGroupRelatedDataDocument,
    CallGroupRelatedDataQuery,
    CallGroupRelatedDataQueryVariables,
    GetNewTargetsDocument,
    GetNewTargetsQuery,
    GetNewTargetsQueryVariables,
} from "@/components/call-group/queries.generated";
import {GroupFilterType} from "@/api";
import {msg_delete} from "@/utils/translations";

const DeleteCallGroupPage = ({
    domainId,
    groupId,
    groupType,
}: {
    domainId: number;
    groupId: number;
    groupType: GroupFilterType;
}) => {
    const {loading, data} = useQuery<CallGroupRelatedDataQuery, CallGroupRelatedDataQueryVariables>(
        CallGroupRelatedDataDocument,
        {
            variables: {groupId},
        },
    );

    const targetsData = useQuery<GetNewTargetsQuery, GetNewTargetsQueryVariables>(GetNewTargetsDocument, {
        variables: {domainId},
    });

    const [pending, setPending] = React.useState(false);
    const form = new DeleteCallGroupForm(domainId, groupId, groupType);

    const exclamationIcon = () => <FontAwesomeIcon style={{color: "red", marginRight: "10px"}} icon="exclamation" />;

    if (loading || targetsData.loading) return <Loading />;

    const handleDelete = async () => {
        setPending(true);
        await form.delete();
        setPending(false);
    };

    function getTarget(referenceType: ReferenceType, excludeNumberId?: number): Target[] {
        if (referenceType == ReferenceType.GATEWAY) {
            const gatewayTargets: Target[] = targetsData.data.users.items.map((extension) => ({
                numberId: extension.number.id,
                displayName: extension.displayName,
                shortNumber: extension.number.shortNumber,
                type: TargetType.EXTENSION,
            }));

            return gatewayTargets.concat(
                targetsData.data.faxes.items.map(
                    (fax): Target => ({
                        numberId: fax.number.id,
                        displayName: null,
                        shortNumber: fax.number.shortNumber,
                        type: TargetType.FAX,
                    }),
                ),
                targetsData.data.ivrs.items.map((ivr) => ({
                    numberId: ivr.number.id,
                    displayName: ivr.name,
                    shortNumber: ivr.number.shortNumber,
                    type: TargetType.IVR,
                })),
                targetsData.data.groups.items
                    .filter((group) => group.id !== groupId)
                    .map(
                        (group): Target => ({
                            numberId: group.number.id,
                            displayName: null,
                            shortNumber: group.number.shortNumber,
                            type: TargetType.GROUP,
                        }),
                    ),
            );
        } else if (referenceType == ReferenceType.USER) {
            const extensionTargets: Target[] = targetsData.data.users.items
                .filter((extension) => extension.number.id !== excludeNumberId)
                .map((extension) => ({
                    numberId: extension.number.id,
                    displayName: extension.displayName,
                    shortNumber: extension.number.shortNumber,
                    type: TargetType.EXTENSION,
                }));

            return extensionTargets.concat(
                targetsData.data.faxes.items.map(
                    (fax): Target => ({
                        numberId: fax.number.id,
                        displayName: null,
                        shortNumber: fax.number.shortNumber,
                        type: TargetType.FAX,
                    }),
                ),
                targetsData.data.ivrs.items.map((ivr) => ({
                    numberId: ivr.number.id,
                    displayName: ivr.name,
                    shortNumber: ivr.number.shortNumber,
                    type: TargetType.IVR,
                })),
                targetsData.data.gateways.items.map((gw) => ({
                    numberId: gw.number.id,
                    displayName: gw.displayName,
                    shortNumber: gw.number.shortNumber,
                    type: TargetType.GW,
                })),
                targetsData.data.groups.items
                    .filter((group) => group.id !== groupId)
                    .map(
                        (group): Target => ({
                            numberId: group.number.id,
                            displayName: null,
                            shortNumber: group.number.shortNumber,
                            type: TargetType.GROUP,
                        }),
                    ),
            );
        } else if (referenceType == ReferenceType.IVR) {
            const ivrTargets: Target[] = targetsData.data.users.items.map((extension) => ({
                numberId: extension.number.id,
                displayName: extension.displayName,
                shortNumber: extension.number.shortNumber,
                type: TargetType.EXTENSION,
            }));

            return ivrTargets.concat(
                targetsData.data.groups.items
                    .filter((group) => group.id !== groupId)
                    .map(
                        (group): Target => ({
                            numberId: group.number.id,
                            displayName: null,
                            shortNumber: group.number.shortNumber,
                            type: TargetType.GROUP,
                        }),
                    ),
                targetsData.data.faxes.items.map(
                    (fax): Target => ({
                        numberId: fax.number.id,
                        displayName: null,
                        shortNumber: fax.number.shortNumber,
                        type: TargetType.FAX,
                    }),
                ),
            );
        }
        throw Error(`Unknown reference type`);
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle desc={defineMessage({id: "delete", defaultMessage: "Delete"})} />
            <Section
                label={defineMessage({
                    id: "call_group_inconsistency.please_solve",
                    defaultMessage: "Please solve the following inconsistencies",
                })}
                hidden={
                    !data.relatedData.gateways.length &&
                    !data.relatedData.overrideCaller.length &&
                    !data.relatedData.ivrActions.length &&
                    !data.relatedData.phoneButtons.length
                }
            >
                <FormContainer>
                    {data.relatedData.gateways.map((gateway, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="call_group_inconsistency.select_gateway"
                                    defaultMessage="Select new callback number for the gateway {name}"
                                    values={{
                                        name: (
                                            <Link to={`/domains/${domainId}/gateways/gateways/${gateway.id}/settings`}>
                                                {gateway.displayName}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Typography>
                            <TargetSelector
                                handleChange={(id) => {
                                    form.handleChange(id, gateway.id, ReferenceType.GATEWAY);
                                }}
                                variant="standard"
                                selected={form.selections[gateway.id]}
                                targets={getTarget(ReferenceType.GATEWAY)}
                                style={TargetSelectorStyle}
                                notSelectedText={defineMessage({id: "clear", defaultMessage: "Clear"})}
                            />
                        </React.Fragment>
                    ))}
                </FormContainer>
                <FormContainer>
                    {data.relatedData.overrideCaller.map((extension, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="call_group_inconsistency.select_extension"
                                    defaultMessage="Select new alternate caller number for the user {name}"
                                    values={{
                                        name: (
                                            <Link to={`/domains/${domainId}/users/${extension.id}/generic`}>
                                                {extension.displayName}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Typography>
                            <TargetSelector
                                handleChange={(id) => {
                                    form.handleChange(id, extension.id, ReferenceType.USER);
                                }}
                                variant="standard"
                                selected={form.selections[extension.id]}
                                targets={getTarget(ReferenceType.USER, extension.number.id)}
                                style={TargetSelectorStyle}
                                notSelectedText={defineMessage({id: "clear", defaultMessage: "Clear"})}
                            />
                        </React.Fragment>
                    ))}
                </FormContainer>
                <FormContainer>
                    {data.relatedData.ivrActions.map((ivr, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="call_group_inconsistency.select_ivr"
                                    defaultMessage="Button {action} in the menu of attendant {ivr} refers to this call group"
                                    values={{
                                        action: ivr.button,
                                        ivr: (
                                            <Link to={`/domains/${domainId}/ivrs/${ivr.ivrProfile.ivr.id}`}>
                                                {ivr.ivrProfile.ivr.name}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Typography>
                            <TargetSelector
                                handleChange={(id) => {
                                    form.handleChange(id, ivr.id, ReferenceType.IVR);
                                }}
                                variant="standard"
                                selected={form.selections[ivr.id]}
                                targets={getTarget(ReferenceType.IVR)}
                                style={TargetSelectorStyle}
                                notSelectedText={defineMessage({id: "clear", defaultMessage: "Clear"})}
                            />
                        </React.Fragment>
                    ))}
                </FormContainer>
                <FormContainer>
                    {data.relatedData.phoneButtons.map((button, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="call_group_inconsistency.phone_button"
                                    defaultMessage="Empty action will be set for button {button} on the device of user {user}"
                                    values={{
                                        extension: (
                                            <Link
                                                to={`/domains/${domainId}/users/${button.device.extensionId}/devices`}
                                            >
                                                {button.device.extension.displayName}
                                            </Link>
                                        ),
                                        button: button.buttonPosition,
                                    }}
                                />
                            </Typography>
                        </React.Fragment>
                    ))}
                </FormContainer>
            </Section>
            <FormattedFormRow>
                <div style={{margin: "1em 0"}}>
                    <FormActionButtons
                        mainButtonText={msg_delete}
                        mainAction={handleDelete}
                        mainButtonColor="secondary"
                        secondaryAction={() => {
                            form.reset();
                        }}
                        secondaryButtonHidden={
                            !data.relatedData.gateways.length &&
                            !data.relatedData.overrideCaller.length &&
                            !data.relatedData.ivrActions.length &&
                            !data.relatedData.phoneButtons.length
                        }
                        pending={pending}
                    />
                </div>
            </FormattedFormRow>
        </div>
    );
};

export default DeleteCallGroupPage;

const TargetSelectorStyle = {
    marginLeft: "25px",
    marginRight: "25px",
    width: "50%",
} as React.CSSProperties;
